import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import EditIcon from "@material-ui/icons/Edit";
import ScheduleIcon from "@material-ui/icons/Schedule";
import ClassNames from "classnames";
import dayjs from "dayjs";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";
import BackButton from "../../components/BackButton";
import Button from "../../components/Button";
import CoachingDateHistory from "../../components/CoachingDateHistory";
import EditCoachingDate from "../../components/EditCoachingDate";
import GentleReminderCard from "../../components/GentleReminderCard";
import NotesSidebar from "../../components/NotesSidebar";
import PageContent from "../../components/PageContent";
import ReviewDateSummary from "../../components/ReviewDateSummary";
import Section from "../../components/Section";
import SectionTitle from "../../components/SectionTitle";
import SessionBasicInfo from "../../components/SessionBasicInfo";
import SessionDeleteButton from "../../components/SessionDeleteButton";
import Stepper from "../../components/Stepper";
import SubjectList from "../../components/SubjectList";
import { Framework, SessionStatus, SessionType } from "../../config/Config";
import {
  IsCampaignManager,
  IsSessionCoach,
  IsSessionCoachee,
  IsSessionReadOnly,
} from "../../config/Roles";
import { useNotification } from "../../context/notification";
import { DashboardLayout } from "../../layouts/DashboardLayout";
import GetSessionData from "../../services/getSessionData";
import GetReportingManagerForSession from "../../services/getSessionReportingManager";
import { getLocalValue } from "../../utils/auth";
import styles from "./SessionsDetails.module.scss";

export function SessionsDetails() {
  const history = useHistory();
  const { id } = useParams();
  const { t } = useTranslation();
  const { addNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(true);
  const [response, setResponse] = useState({});
  const [sessionData, setSessionData] = useState({});
  const [reportingManager, setReportingManager] = useState("");
  const [subjectList, setSubjectList] = useState([]);
  const [sessionCoach, setSessionCoach] = useState(null);
  const [sessionCoachee, setSessionCoachee] = useState(null);
  const [sessionDuration, setSessionDuration] = useState(0);
  const [sessionType, setSessionType] = useState(null);
  const [sessionStatus, setSessionStatus] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [isDateEdited, setIsDateEdited] = useState(false);
  const [coachingDateHistoryList, setCoachingDateHistoryList] = useState([]);
  const [hideProjectTargetKpi, sethideProjectTargetKpi] = useState(false);
  const [hideCurrentMetrics, sethideCurrentMetricsi] = useState(false);

  useEffect(() => {
    // fetch from getSessionDetails api
    async function fetchData() {
      setIsLoading(true);
      const sessionAPIData = await GetSessionData(id);
      const reportingManagerAPIData = await GetReportingManagerForSession(id);
      if (sessionAPIData) {
        setResponse(sessionAPIData);
      } else {
        addNotification(`Could not get session ${id}`, "error");
      }

      if (reportingManagerAPIData) {
        setReportingManager(reportingManagerAPIData);
      }
      setIsLoading(false);
    }
    fetchData();
  }, [id, isDateEdited]);

  // helper function to get values under "session" in data object
  const getSessionValue = field => {
    return sessionData ? sessionData[field] : undefined;
  };

  useEffect(() => {
    if (response.subject && response.subject.length > 0) {
      setSubjectList(response.subject);
      //
    }
    if (
      response?.coaching_date_history &&
      response.coaching_date_history.length > 0
    ) {
      setCoachingDateHistoryList(response.coaching_date_history);
    }
    setSessionData(response.session);
  }, [response]);

  useEffect(() => {
    if (response.session && response.session.session_type_id === 2) {
      sethideProjectTargetKpi(true);
      sethideCurrentMetricsi(true);
      // console.log(response.session_type_id,'response')
    } else {
      sethideProjectTargetKpi(false);
      sethideCurrentMetricsi(false);
    }
  }, [response]);

  useEffect(() => {
    setSessionCoach(IsSessionCoach(getSessionValue("coach_id")));
    setSessionCoachee(IsSessionCoachee(getSessionValue("coachee_id")));
    setSessionStatus(getSessionValue("status"));
    setSessionType(getSessionValue("session_type_id"));
  }, [sessionData]);

  // get current session duration for the stepper
  const getTotalTime = time => {
    setSessionDuration(time);
  };

  const isReadOnly = useMemo(() => {
    if (_.isEmpty(sessionData)) return true;
    return IsSessionReadOnly(sessionData.coach_id, sessionData.coachee_id);
  }, [sessionData]);

  return (
    <DashboardLayout
      appBarContent={
        <Stepper
          isPDFlow={getSessionValue("framework") === Framework.PD}
          phase={t("header.stepper.plan")}
          phaseLabel="Plan"
          sessionId={id}
          clockRun={false}
          getTotalTimeSpent={getTotalTime}
        />
      }
      rightSidebarContent={
        <NotesSidebar
          sessionID={id}
          isCoach={sessionCoach}
          accessible={!isReadOnly}
        />
      }
    >
      <div className={styles.pageWrapper}>
        <PageContent indent>
          <Section>
            <div className={styles.detailsContainer}>
              <BackButton text={t("common.backToSession")} />
              <SectionTitle
                append={
                  <>
                    {getLocalValue("employee_id") === reportingManager &&
                      IsCampaignManager() && (
                        <SessionDeleteButton sessionId={id} />
                      )}

                    {sessionCoach &&
                      (sessionStatus === SessionStatus.PENDING_CONFIRMATION ||
                        sessionType === SessionType.Conversational) && (
                        <div
                          className={styles.sectionTitleEditBtn}
                          onClick={() => {
                            setEditModalOpen(true);
                            setIsDateEdited(false);
                          }}
                        >
                          <EditIcon /> {t("growForm.edit")}
                        </div>
                      )}
                  </>
                }
              >
                {t("sessionDetails.sessionDetails")}
              </SectionTitle>
              <SessionBasicInfo sessionData={sessionData} />

              <CoachingDateHistory
                sessionData={sessionData}
                sessionDateHistory={coachingDateHistoryList}
              />
              {coachingDateHistoryList.length === 0 &&
                //If PD Review Date will not show
                getSessionValue("framework") === Framework.PDCA && (
                  <div className={styles.row}>
                    <div className={styles.rowLeft}>
                      {t("sessionDetails.reviewDate")}
                    </div>
                    <div className={styles.rowRight}>
                      <span className={styles.rowIcon}>
                        <CalendarTodayIcon />
                      </span>
                      <span style={{ padding: "0 12px 0 4px" }}>
                        {/* Time format: 9 Jan 2020 */}
                        {dayjs(getSessionValue("review_datetime"))
                          .add(dayjs().utcOffset(), "minutes")
                          .format("D MMM YYYY")}
                      </span>
                      <span className={styles.rowIcon}>
                        <ScheduleIcon />
                      </span>
                      <span style={{ padding: "0 12px 0 4px" }}>
                        {/* Time format: 8:12 PM*/}
                        {dayjs(getSessionValue("review_datetime"))
                          .add(dayjs().utcOffset(), "minutes")
                          .format("h:mm A")}
                      </span>
                    </div>
                  </div>
                )}
              {coachingDateHistoryList.length > 0 &&
                //If PD Review Date will not show
                getSessionValue("framework") === Framework.PDCA && (
                  <div style={{ marginBottom: "20px" }}>
                    {" "}
                    <ReviewDateSummary id={id} updated={isDateEdited} />{" "}
                  </div>
                )}
              <div className={styles.row}>
                <div className={styles.rowLeft}>
                  {t("sessionDetails.typeofSession")}
                </div>
                <div className={styles.rowRight}>
                  {getSessionValue("session_type")}
                </div>
              </div>
              <div className={ClassNames(styles.row, styles.noPaddingBottom)}>
                <div
                  className={ClassNames(
                    styles.rowLeft,
                    styles.subjectListLabel
                  )}
                >
                  {t("sessionDetails.subjects")}
                </div>
                <div
                  className={ClassNames(
                    styles.rowRight,
                    styles.subjectListWrapper
                  )}
                >
                  <SubjectList
                    list={subjectList}
                    hideProjectTargetKpi={hideProjectTargetKpi}
                    hideCurrentMetrics={hideCurrentMetrics}
                  />
                </div>
              </div>
            </div>
          </Section>
          <Section>
            <div className={styles.detailsContainer}>
              <GentleReminderCard
                label={t("sessionDetails.gentleReminderTitle")}
                description={t("sessionDetails.gentleReminder")}
              />
              {sessionCoachee &&
                sessionData?.session_type_id !== SessionType.Conversational &&
                sessionStatus === SessionStatus.PENDING_CONFIRMATION && (
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    onClick={() => {
                      history.push(`/sessions/grow/${id}`);
                    }}
                  >
                    {t("sessionDetails.startPreparation")}
                  </Button>
                )}
              {sessionCoachee &&
                sessionData?.session_type_id === SessionType.Conversational &&
                sessionStatus === SessionStatus.SCHEDULED && (
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    onClick={() => {
                      history.push(`/sessions/conversational/${id}`);
                    }}
                  >
                    {t("growForm.startCoachingSession")}
                  </Button>
                )}
            </div>
          </Section>

          <EditCoachingDate
            isOpen={editModalOpen}
            setEditModalOpen={setEditModalOpen}
            sessionDetails={sessionData}
            subjectDetails={subjectList}
            setSubjectDetails={setSubjectList}
            setIsDateEdited={setIsDateEdited}
            coachingDateHistoryList={coachingDateHistoryList}
            hideProjectTargetKpi={hideProjectTargetKpi}
          />
        </PageContent>
      </div>
    </DashboardLayout>
  );
}
